import React, { useEffect, useState, useCallback } from "react";
import { Check, X, Trash2, RefreshCcw, Inbox, CheckCircle2, XCircle, Reply, Save, Edit3 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUS_TABS = [
    { id: "pending", label: "Bekleyen", icon: Inbox },
    { id: "approved", label: "Onaylı", icon: CheckCircle2 },
    { id: "rejected", label: "Reddedildi", icon: XCircle },
    { id: "all", label: "Tümü", icon: RefreshCcw },
];

const ScoreCell = ({ value }) => {
    const v = Number(value) || 0;
    const cls = v >= 8 ? "bg-emerald-600 text-white" : v >= 6 ? "bg-emerald-700/80 text-white" : v >= 4 ? "bg-amber-600/90 text-white" : "bg-rose-600/80 text-white";
    return <span className={`${cls} font-mono font-bold text-sm px-2 py-0.5 rounded-sm`}>{v.toFixed(1)}</span>;
};

const AdminReviews = () => {
    const [status, setStatus] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [replyDrafts, setReplyDrafts] = useState({});      // { reviewId: text }
    const [openReplyId, setOpenReplyId] = useState(null);
    const [savingReplyId, setSavingReplyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = status === "all" ? {} : { status };
            const { data } = await api.get("/admin/reviews", { params });
            setItems(data);
        } catch (e) {
            toast.error("Yorumlar yüklenemedi");
        } finally { setLoading(false); }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (id, newStatus) => {
        try {
            await api.patch(`/admin/reviews/${id}`, { status: newStatus });
            toast.success(newStatus === "approved" ? "Yorum yayınlandı" : "Yorum reddedildi");
            load();
        } catch {
            toast.error("İşlem başarısız");
        }
    };

    const remove = async (id) => {
        if (!confirm("Bu yorumu silmek istiyor musunuz?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            toast.success("Yorum silindi");
            load();
        } catch {
            toast.error("Silinemedi");
        }
    };

    const openReply = (r) => {
        setOpenReplyId(r.id);
        setReplyDrafts((d) => ({ ...d, [r.id]: r.admin_reply || "" }));
    };

    const saveReply = async (id) => {
        const text = (replyDrafts[id] || "").trim();
        setSavingReplyId(id);
        try {
            await api.patch(`/admin/reviews/${id}`, { admin_reply: text });
            toast.success(text ? "Cevap kaydedildi" : "Cevap kaldırıldı");
            setOpenReplyId(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cevap kaydedilemedi");
        } finally {
            setSavingReplyId(null);
        }
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Yorum Yönetimi</h1>
                    <p className="text-muted_green text-sm">Misafir yorumlarını onaylayın veya reddedin. Sadece onaylı yorumlar otel sayfasında görünür.</p>
                </div>
                <button onClick={load} className="btn-outline-gold !text-xs" data-testid="reviews-refresh-btn">
                    <RefreshCcw className="w-3.5 h-3.5" /> Yenile
                </button>
            </div>

            <div className="flex gap-px bg-border_green mb-6 max-w-2xl">
                {STATUS_TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setStatus(t.id)}
                            className={`flex-1 px-4 py-3 text-xs uppercase tracking-[0.18em] flex items-center justify-center gap-2 transition-colors ${
                                status === t.id ? "bg-gold text-emerald_deep font-bold" : "bg-emerald_surface text-bone/80 hover:bg-emerald_hover"
                            }`}
                            data-testid={`reviews-tab-${t.id}`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center" data-testid="reviews-empty">
                    <Inbox className="w-12 h-12 text-gold/60 mx-auto mb-3" />
                    <div className="font-serif text-xl mb-1">Bu sekmede yorum yok</div>
                    <div className="text-sm text-muted_green">Yeni yorumlar geldiğinde burada listelenecek.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((r) => (
                        <div key={r.id} className="border border-border_green bg-emerald_surface p-5" data-testid={`admin-review-${r.id}`}>
                            <div className="flex items-start justify-between flex-wrap gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-serif text-lg">{r.customer_name}</span>
                                        <ScoreCell value={r.rating} />
                                        <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
                                            r.status === "approved" ? "border-green-500/40 text-green-400" :
                                            r.status === "rejected" ? "border-red-500/40 text-red-400" :
                                            "border-gold/40 text-gold"
                                        }`}>
                                            {r.status}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-muted_green mt-1">
                                        {r.hotel_name} · {new Date(r.created_at).toLocaleString("tr-TR")}
                                        {r.customer_phone && ` · ${r.customer_phone}`}
                                    </div>
                                    <p className="text-sm mt-3 whitespace-pre-wrap leading-relaxed">{r.comment}</p>

                                    {/* Existing admin reply preview */}
                                    {r.admin_reply && openReplyId !== r.id && (
                                        <div className="mt-3 border-l-2 border-gold/50 pl-3 bg-emerald_deep/40 p-3" data-testid={`admin-reply-preview-${r.id}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <Reply className="w-3 h-3 text-gold" />
                                                <span className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">Otel Cevabı</span>
                                                {r.admin_reply_at && (
                                                    <span className="text-[10px] text-muted_green">· {new Date(r.admin_reply_at).toLocaleDateString("tr-TR")}</span>
                                                )}
                                            </div>
                                            <p className="text-xs leading-relaxed text-bone/85 whitespace-pre-wrap">{r.admin_reply}</p>
                                        </div>
                                    )}

                                    {/* Reply editor */}
                                    {openReplyId === r.id ? (
                                        <div className="mt-3 border border-gold/40 bg-emerald_deep/40 p-3" data-testid={`reply-editor-${r.id}`}>
                                            <div className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold mb-2 flex items-center gap-1.5">
                                                <Reply className="w-3 h-3" /> Otel Olarak Cevap Yaz
                                            </div>
                                            <textarea
                                                rows={3}
                                                maxLength={1500}
                                                value={replyDrafts[r.id] || ""}
                                                onChange={(e) => setReplyDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                                                placeholder="Misafire kibar ve profesyonel bir yanıt yazın..."
                                                className="input-dark text-xs"
                                                data-testid={`reply-textarea-${r.id}`}
                                            />
                                            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                                                <span className="text-[10px] text-muted_green">{(replyDrafts[r.id] || "").length}/1500</span>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => setOpenReplyId(null)}
                                                        className="text-[11px] text-bone/70 hover:text-bone uppercase tracking-[0.15em]"
                                                        data-testid={`reply-cancel-${r.id}`}
                                                    >
                                                        İptal
                                                    </button>
                                                    <button
                                                        onClick={() => saveReply(r.id)}
                                                        disabled={savingReplyId === r.id}
                                                        className="btn-gold !text-[11px] !py-1.5 !px-3"
                                                        data-testid={`reply-save-${r.id}`}
                                                    >
                                                        <Save className="w-3 h-3" /> {savingReplyId === r.id ? "Kaydediliyor…" : "Kaydet"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => openReply(r)}
                                            className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-gold hover:underline"
                                            data-testid={`open-reply-${r.id}`}
                                        >
                                            {r.admin_reply ? <Edit3 className="w-3 h-3" /> : <Reply className="w-3 h-3" />}
                                            {r.admin_reply ? "Cevabı Düzenle" : "Cevap Yaz"}
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                    {r.status !== "approved" && (
                                        <button
                                            onClick={() => updateStatus(r.id, "approved")}
                                            className="btn-gold !text-xs !py-2"
                                            data-testid={`approve-review-${r.id}`}
                                        >
                                            <Check className="w-3.5 h-3.5" /> Onayla
                                        </button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button
                                            onClick={() => updateStatus(r.id, "rejected")}
                                            className="btn-outline-gold !text-xs !py-2"
                                            data-testid={`reject-review-${r.id}`}
                                        >
                                            <X className="w-3.5 h-3.5" /> Reddet
                                        </button>
                                    )}
                                    <button
                                        onClick={() => remove(r.id)}
                                        className="text-[11px] text-red-400 hover:text-red-500 inline-flex items-center gap-1 justify-center mt-1"
                                        data-testid={`delete-review-${r.id}`}
                                    >
                                        <Trash2 className="w-3 h-3" /> Sil
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminReviews;
